import { FormikProps } from 'formik';
import { filter, flatMap, isEmpty, map } from 'lodash';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { Expandable } from '../../shared/Expandable';
import { ExpandControlChevron } from '../../shared/ExpandControlChevron';
import { SearchSelectOptions } from '../../shared/form/inputs/SearchSelect';
import { getNullSelectOption } from '../../shared/form/inputs/Select';
import { TextAreaField } from '../../shared/form/inputs/TextAreaField';
import { Header2 } from '../../shared/Headers';
import { colourNeutral08, colourNeutral09 } from '../../styling/colours';
import { spacing06, spacing07 } from '../../styling/spacing';
import { EditDesignBillOfMaterialsFormModel } from '../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import { SalesOrderNotes } from '../designs/edit-bill-of-materials/SalesOrderNotes';
import { BillOfMaterialsDiscountFields } from '../quotes/view-or-edit-bill-of-materials/BillOfMaterialsDiscountFields';
import { BillOfMaterialsUnitsTable } from '../quotes/view-or-edit-bill-of-materials/BillOfMaterialsUnitsTable';
import {
  ViewOrEditQuoteBillOfMaterialsFormModel,
  ManualDiscountTypeCode,
} from '../quotes/view-or-edit-bill-of-materials/viewOrEditQuoteBillOfMaterialsFormModel';
import { BillOfMaterialsFormHeader } from './BillOfMaterialsFormHeader';
import { BillOfMaterialsOptionalExtraFormModel } from './billOfMaterialsOptionalExtraFormModel';
import { BillOfMaterialsOptionalExtrasField } from './BillOfMaterialsOptionalExtrasField';
import { BillOfMaterialsRemovedItemsField } from './BillOfMaterialsRemovedItemsField';
import { BillOfMaterialsUnitFields } from './BillOfMaterialsUnitFields';
import { BillOfMaterialsUnitFormModel } from './billOfMaterialsUnitFormModel';
import { BillOfMaterialsPartsById } from './dataForEditBillOfMaterialsForm';

type Props<TFormModel extends EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel> = {
  discountPercentage?: number | null;
  manualDiscountAmount?: number | null;
  manualDiscountTypeCode?: ManualDiscountTypeCode;
  units: Array<BillOfMaterialsUnitFormModel>;
  heatPumpOptionalExtras: Array<BillOfMaterialsOptionalExtraFormModel>;
  partsById: BillOfMaterialsPartsById;
  setFieldValue: FormikProps<TFormModel>['setFieldValue'];
  setFieldTouched: FormikProps<TFormModel>['setFieldTouched'];
  errors: FormikProps<TFormModel>['errors'];
  salesOrderNotes?: string | null;
  isQuote: boolean;
};

export const HeatPumpBillOfMaterialsFields = <
  TFormModel extends EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel
>({
  discountPercentage,
  manualDiscountAmount,
  manualDiscountTypeCode,
  units,
  heatPumpOptionalExtras,
  partsById,
  setFieldValue,
  setFieldTouched,
  errors,
  salesOrderNotes,
  isQuote,
}: Props<TFormModel>) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const partsOptions: SearchSelectOptions<number | null> = useMemo(() => {
    const partIds = Object.keys(partsById)
      .map(partId => Number(partId))
      .filter(partId => !partsById[partId].isInactive);

    const options = partIds.map((partId: number) => ({
      text: partsById[partId].name,
      value: partId,
      searchableText: `${partsById[partId].name} - ${partsById[partId].description}`,
    }));

    return [getNullSelectOption<number>(), ...options];
  }, [partsById]);

  return (
    <Container>
      <BillOfMaterialsFormHeader>
        <ExpandControlChevron isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
        <Header2>Heat Pump Bill of Materials</Header2>
      </BillOfMaterialsFormHeader>
      <Expandable isExpanded={isExpanded} animationDurationSeconds={0.5}>
        {isQuote && (
          <>
            <TextAreaField name="heatPumpQuoteDescription" label="Quote Description" expandable={true} />
            <BillOfMaterialsUnitsTable
              units={units}
              itemsFieldName="heatPumpItems"
              descriptionFieldName="heatPumpDescription"
              partsById={partsById}
            />
          </>
        )}
        {map(
          filter(
            map(units, (unit, index) => ({ unit, unitIndex: index })),
            ({ unit }) => !isEmpty(unit.heatPumpItems),
          ),
          ({ unit, unitIndex }) => (
            <BillOfMaterialsUnitFields
              unit={unit}
              unitIndex={unitIndex}
              itemsFieldName="heatPumpItems"
              partsById={partsById}
              partsOptions={partsOptions}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              errors={errors}
              isQuote={isQuote}
            />
          ),
        )}

        {manualDiscountAmount !== undefined && manualDiscountTypeCode !== undefined && (
          <SummarySection>
            <BillOfMaterialsDiscountFields
              discountPercentage={discountPercentage}
              discountPercentageFieldName="heatPumpDiscountPercentage"
              manualDiscountAmountFieldName="heatPumpManualDiscountAmount"
              manualDiscountTypeFieldName="heatPumpManualDiscountTypeCode"
              manualDiscountAmount={manualDiscountAmount}
              manualDiscountTypeCode={manualDiscountTypeCode}
              items={flatMap(units, unit => unit.heatPumpItems)}
              partsById={partsById}
              setFieldValue={setFieldValue}
            />
          </SummarySection>
        )}

        <Section>
          <BillOfMaterialsRemovedItemsField
            itemsFieldName="heatPumpItems"
            units={units}
            partsById={partsById}
            setFieldValue={setFieldValue}
          />
        </Section>

        <Section>
          <BillOfMaterialsOptionalExtrasField
            optionalExtrasFieldArrayName="heatPumpOptionalExtras"
            optionalExtras={heatPumpOptionalExtras}
            label="Upgrade Options"
            partsById={partsById}
            partsOptions={partsOptions}
            errors={errors}
          />
        </Section>

        {salesOrderNotes !== undefined && (
          <Section>
            <SalesOrderNotes notes={salesOrderNotes} />
          </Section>
        )}
      </Expandable>
    </Container>
  );
};

const Container = styled.div`
  margin: ${spacing07} 0;
`;

const Section = styled.div`
  margin: ${spacing07} 0;
`;

const SummarySection = styled(Section)`
  padding: ${spacing06} ${spacing06} 0 ${spacing06};
  border: solid 1px ${colourNeutral08};
  background-color: ${colourNeutral09};
`;
